import type { ActionLinkUnavailableReason } from "./generated/actionLinks";
import type { VerifiedOAuthVendorKey } from "./generated/publicMcpOAuth";

import { parseSchemaOrNull } from "replo-utils/lib/parse";

import { verifiedOAuthVendorKeySchema } from "./publicMcpOAuth";

/** Where a link that can't be used lands. It needs no sign-in: it only says why and points back. */
export function getActionLinkUnavailablePageUrl({
  dashboardBaseUrl,
  reason,
}: {
  dashboardBaseUrl: string;
  reason: ActionLinkUnavailableReason;
}) {
  return new URL(`/action/unavailable/${reason}`, dashboardBaseUrl).toString();
}

/** The chat apps Replo can name on a link's pages; any other app is "your chat". */
export const ACTION_LINK_CHAT_APP_NAMES = {
  claude: "Claude",
  chatgpt: "ChatGPT",
  muse: "Muse",
} as const satisfies Record<VerifiedOAuthVendorKey, string>;

/**
 * The dashboard page a link opens on, where the person signs in and finishes
 * the step. `chatApp` rides along so the sign-in screen, which can't read the
 * link before sign-in, can name the chat too; it only picks words.
 */
export function getActionLinkPageUrl({
  dashboardBaseUrl,
  token,
  chatApp,
  checkout,
}: {
  dashboardBaseUrl: string;
  token: string;
  chatApp: VerifiedOAuthVendorKey | null;
  /** Stripe Checkout's return, so the page says nothing was charged or waits for the payment; null otherwise. */
  checkout: "canceled" | "paid" | null;
}) {
  const url = new URL(`/action/${encodeURIComponent(token)}`, dashboardBaseUrl);
  if (chatApp) {
    url.searchParams.set("chat", chatApp);
  }
  if (checkout) {
    url.searchParams.set("checkout", checkout);
  }
  return url.toString();
}

/** The chat apps' own Connect windows return to these https hosts; it only picks the words on the sign-in screen. */
const CHAT_APP_CALLBACK_HOSTS = new Map<string, VerifiedOAuthVendorKey>([
  ["claude.ai", "claude"],
  ["claude.com", "claude"],
  ["chatgpt.com", "chatgpt"],
  ["platform.muse.ai", "muse"],
]);

/**
 * For a sign-in screen that sends the person on to `next`: whether a chat
 * sent them, and which. `finish` is a link's page, `connect` a known chat
 * app's own Connect window (the OAuth authorize page). Null for anything
 * else, including another site's address and an app that isn't a chat.
 */
export function findChatSignInNext(next: string | null) {
  const base = "https://next.invalid";
  if (!next || !URL.canParse(next, base)) {
    return null;
  }
  const url = new URL(next, base);
  if (url.origin !== base) {
    return null;
  }
  if (url.pathname === "/oauth/authorize") {
    const redirectUri = url.searchParams.get("redirect_uri") ?? "";
    const redirect = URL.canParse(redirectUri) ? new URL(redirectUri) : null;
    const chat =
      redirect?.protocol === "https:"
        ? CHAT_APP_CALLBACK_HOSTS.get(redirect.host)
        : undefined;
    return chat
      ? {
          purpose: "connect" as const,
          chatName: ACTION_LINK_CHAT_APP_NAMES[chat],
        }
      : null;
  }
  if (
    !url.pathname.startsWith("/action/") ||
    url.pathname.startsWith("/action/unavailable/")
  ) {
    return null;
  }
  const chat = parseSchemaOrNull(
    url.searchParams.get("chat"),
    verifiedOAuthVendorKeySchema,
  );
  return {
    purpose: "finish" as const,
    chatName: chat ? ACTION_LINK_CHAT_APP_NAMES[chat] : null,
  };
}

/** The sign-in heading when a chat sent the person here, naming it; null otherwise. */
export function getChatSignInTitle(next: string | null) {
  const chatSignIn = findChatSignInNext(next);
  if (!chatSignIn) {
    return null;
  }
  const chatName = chatSignIn.chatName ?? "your chat";
  return chatSignIn.purpose === "connect"
    ? `Sign in to connect ${chatName}`
    : `Sign in to finish in ${chatName}`;
}
